import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowLeftIcon,
  CircleCheckIcon,
  Copy,
  Download,
  EditIcon,
  MapPin,
  Phone,
  Receipt,
  RotateCcw,
  ShoppingBag,
  Truck,
  UserIcon,
  XCircle,
} from "@/components/icons";
import {
  Badge,
  Button,
  Input,
  Skeleton,
  confirm,
  toast,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useChatSnippetsQuery,
  useOrderChatSummaryQuery,
} from "@/modules/chat-tools/presentation/api/queries";
import { copyToClipboard } from "@/modules/chat-tools/presentation/lib/copy-to-clipboard";
import { canEditOrderDetails } from "@/modules/orders/domain/order-editable";
import { displayCustomerAddress } from "@/modules/customers/domain/address";
import {
  canRevertOrderStatus,
  getPreviousStatus,
} from "@/modules/orders/domain/order-status-revert";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import {
  useCancelOrder,
  useMarkOrderDelivered,
  useMarkOrderPaid,
  useMarkOrderPickedUp,
  useMarkOrderReady,
  useMarkOrderShipped,
  useOrderQuery,
  useReopenOrderForShipment,
  useRevertOrderStatus,
} from "../api/queries";
import { TrackingBarcodeDialog } from "../ui/TrackingBarcodeDialog";
import { OrderDetailsEditDialog } from "../ui/OrderDetailsEditDialog";
import { OrderDetailItemsPanel } from "../ui/OrderDetailItemsPanel";
import { OrderMarkReturnedDialog } from "../ui/OrderMarkReturnedDialog";
import { ProductFormSectionLike } from "../ui/OrderSection";
import {
  DELIVERY_METHOD_META,
  PAYMENT_TYPE_META,
  formatDeliverySummary,
  getOrderStatusMeta,
  orderStatusLabel,
  paymentTypeLabel,
  revertStatusDescription,
} from "../ui/order-status";

export function OrderDetailPage() {
  const { id } = useParams({ from: "/app/orders/$id" });
  const nav = useNavigate();
  const canManage = useActionPermission(["orders:write"]);
  const query = useOrderQuery(id);
  const summaryQuery = useOrderChatSummaryQuery(id);
  const snippetsQuery = useChatSnippetsQuery();
  const markPaid = useMarkOrderPaid();
  const markReady = useMarkOrderReady();
  const markShipped = useMarkOrderShipped();
  const markDelivered = useMarkOrderDelivered();
  const markPickedUp = useMarkOrderPickedUp();
  const reopenShipment = useReopenOrderForShipment();
  const revertStatus = useRevertOrderStatus();
  const cancelOrder = useCancelOrder();
  const [trackingInput, setTrackingInput] = useState("");
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [detailsEditOpen, setDetailsEditOpen] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);

  const order = query.data;

  if (query.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-60 rounded-xl" />
        </Main>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Header />
        <Main className="flex flex-col items-center gap-4 py-20 text-center">
          <Receipt className="size-12 text-muted-foreground/50" />
          <p className="text-muted-foreground">ບໍ່ພົບອໍເດີ້ນີ້</p>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => nav({ to: "/app/orders" })}
          >
            ກັບໄປລາຍການ
          </Button>
        </Main>
      </>
    );
  }

  const meta = getOrderStatusMeta(order.status, order.paymentType);
  const isDraft = order.status === "draft";
  const isPaid = order.status === "paid";
  const isReadyToShip = order.status === "ready_to_ship";
  const isShipped = order.status === "shipped";
  const isDelivered = order.status === "delivered";
  const isPickedUp = order.status === "picked_up";
  const isReturned = order.status === "returned";
  const isCancelled = order.status === "cancelled";
  const canMarkReturned = isReadyToShip || isShipped || isDelivered;
  const canEditDetails = canManage && canEditOrderDetails(order.status);
  const canRevert = canManage && canRevertOrderStatus(order.status);
  const previousStatus = canRevert ? getPreviousStatus(order) : null;
  const customerAddress = displayCustomerAddress(order.customer);
  const showCogs =
    !isDraft &&
    !isCancelled &&
    order.items.some(
      (item) =>
        (item.cogsAllocations?.length ?? 0) > 0 ||
        (item.missingLotQuantity ?? 0) > 0,
    );

  async function handleMarkPaid() {
    const isCod = order.paymentType === "cod";
    const ok = await confirm({
      title: isCod ? "ຢືນຢັນອໍເດີ້ COD?" : "ຢືນຢັນ ຊຳລະແລ້ວ?",
      description: isCod
        ? "ຢືນຢັນເພື່ອແພັກ/ສົ່ງ — ລູກຄ້າຈ່າຍເມື່ອຮັບ. ລະບົບຈະຫັກສະຕັອກ."
        : "ລະບົບຈະຫັກສະຕັອກສິນຄ້າອັດຕະໂນມັດ",
      actionText: isCod ? "ຢືນຢັນ COD" : "ຊຳລະແລ້ວ",
    });
    if (!ok) return;
    markPaid.mutate(id, {
      onSuccess: () =>
        toast.success(isCod ? "ຢືນຢັນ COD ແລ້ວ — ລໍຖ້າແພັກ" : "ອັບເດດເປັນ ຊຳລະແລ້ວ ແລ້ວ"),
      onError: (e) =>
        toast.error("ຊຳລະບໍ່ສຳเร็จ", {
          description: e instanceof Error ? e.message : undefined,
        }),
    });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: "ຍົກເລີກອໍເດີ້?",
      description: isDraft
        ? "ອໍເດີ້ຮ່າງຈະถูกยกเลิก"
        : isReturned
          ? "ສະຕັອກຄືນແລ້ວຕອນຕີກັບ — ຍົກເລີກເພື່ອປິດອໍເດີ້"
          : "ຈະຄືນສະຕັອກສິນຄ້າທີ່ຫັກໄປ",
      actionText: "ຍົກເລີກອໍເດີ້",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    cancelOrder.mutate(id, {
      onSuccess: () => toast.success("ຍົກເລີກອໍເດີ້ແລ້ວ"),
      onError: (e) =>
        toast.error("ຍົກເລີກບໍ່ສຳເร็จ", {
          description: e instanceof Error ? e.message : undefined,
        }),
    });
  }

  async function handleMarkReady() {
    const ok = await confirm({
      title: "ແພັກສຳເລັດແລ້ວ?",
      description: "ຍ້າຍໄປລໍຖ້າສົ່ງທີ່ຂົນສົ່ງ",
      actionText: "ແພັກສຳເລັດ",
    });
    if (!ok) return;
    markReady.mutate(
      { id },
      {
        onSuccess: () => toast.success("ພ້ອມສົ່ງແລ້ວ"),
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  async function handleMarkShipped() {
    const ok = await confirm({
      title: "ຢືນຢັນສົ່ງແລ້ວ?",
      description: "ບັນທຶກວ່າສິນຄ້າສົ່ງໄປຂົນສົ່ງແລ້ວ",
      actionText: "ສົ່ງແລ້ວ",
    });
    if (!ok) return;
    markShipped.mutate(
      {
        id,
        input: { trackingNumber: trackingInput.trim() || null },
      },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກສົ່ງແລ້ວ");
          setTrackingInput("");
        },
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  async function handleRevertStatus() {
    if (!previousStatus) return;
    const ok = await confirm({
      title: "ຍ້ອນສະຖານະ?",
      description: revertStatusDescription(order.status, previousStatus),
      actionText: "ຍ້ອນກັບ",
      ActionProps: { variant: "outline" },
    });
    if (!ok) return;
    revertStatus.mutate(id, {
      onSuccess: () =>
        toast.success(
          `ຍ້ອນເປັນ ${orderStatusLabel(previousStatus, order.paymentType)} ແລ້ວ`,
        ),
      onError: (e) =>
        toast.error("ຍ້ອນສະຖານະບໍ່ສຳເລັດ", {
          description: e instanceof Error ? e.message : undefined,
        }),
    });
  }

  async function handleMarkReturned() {
    setReturnDialogOpen(true);
  }

  async function handleMarkDelivered() {
    const ok = await confirm({
      title: "ຂົນສົ່ງສົ່ງຮອດແລ້ວ?",
      description: "ບັນທຶກວ່າຂົນສົ່ງສົ່ງສິນຄ້າຮອດປາຍທາງແລ້ວ — ລໍຖ້າລູກຄ້າໄປເອົາ",
      actionText: "ສົ່ງຮອດແລ້ວ",
    });
    if (!ok) return;
    markDelivered.mutate(
      { id },
      {
        onSuccess: () => toast.success("ບັນທຶກຂົນສົ່ງສົ່ງຮອດແລ້ວ"),
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  async function handleMarkPickedUp() {
    const isCod = order.paymentType === "cod";
    const ok = await confirm({
      title: "ລູກຄ້າໄປເອົາແລ້ວ?",
      description: isCod
        ? "ບັນທຶກວ່າລູກຄ້າໄປຮັບແລ້ວ ແລະ ຮັບເງິນ COD ແລ້ວ"
        : "ບັນທຶກວ່າລູກຄ້າໄປຮັບສິນຄ້າແລ້ວ",
      actionText: "ລູກຄ້າໄປເອົາແລ້ວ",
    });
    if (!ok) return;
    markPickedUp.mutate(
      { id },
      {
        onSuccess: () =>
          toast.success(isCod ? "ປິດອໍເດີ້ແລ້ວ — ບັນທຶກຮັບເງິນ COD" : "ປິດອໍເດີ້ແລ້ວ"),
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  async function handleReopenShipment() {
    const ok = await confirm({
      title: "ສົ່ງໃໝ່?",
      description: "ຫັກສະຕັອກອີກຄັ້ງ ແລະ ກັບໄປລໍຖ້າແພັກ",
      actionText: "ສົ່ງໃໝ່",
    });
    if (!ok) return;
    reopenShipment.mutate(
      { id },
      {
        onSuccess: () => toast.success("ພ້ອມສົ່ງໃໝ່ແລ້ວ"),
        onError: (e) =>
          toast.error("ດຳເນີນການບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  async function copySummary() {
    let text = summaryQuery.data?.text;
    if (!text) {
      const fresh = await summaryQuery.refetch();
      text = fresh.data?.text;
    }
    if (!text) {
      toast.error("ບໍ່ສາມາດໂຫຼດສະຫຼຸບໄດ້");
      return;
    }
    const ok = await copyToClipboard(text);
    if (ok) toast.success("Copy ສະຫຼຸບແຊດແລ້ວ");
    else toast.error("Copy ບໍ່ສຳເລັດ");
  }

  async function copySnippet(body: string) {
    const ok = await copyToClipboard(body);
    if (ok) toast.success("Copy snippet ແລ້ວ");
    else toast.error("Copy ບໍ່ສຳເລັດ");
  }

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-2xl text-foreground sm:text-3xl">
                {order.orderNumber}
              </h2>
              <Badge variant={meta?.variant ?? "outline"}>
                {orderStatusLabel(order.status, order.paymentType)}
              </Badge>
              <Badge variant={PAYMENT_TYPE_META[order.paymentType].variant}>
                {PAYMENT_TYPE_META[order.paymentType].shortLabel}
              </Badge>
              <Badge
                variant={DELIVERY_METHOD_META[order.deliveryMethod].variant}
              >
                {DELIVERY_METHOD_META[order.deliveryMethod].shortLabel}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              ສ້າງເມື່ອ {formatDateTimeLocal(order.createdAt)}
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="shrink-0 rounded-full border-secondary/40"
            onClick={() => nav({ to: "/app/orders" })}
          >
            <ArrowLeftIcon data-icon="inline-start" />
            ກັບຄืน
          </Button>
        </div>

        <ProductFormSectionLike
          icon={UserIcon}
          title="ລູກຄ້າ"
          action={
            <div className="flex flex-wrap gap-2">
              {canEditDetails ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setDetailsEditOpen(true)}
                >
                  <EditIcon data-icon="inline-start" />
                  ແກ້ໄຂຂໍ້ມູນ
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() =>
                  nav({
                    to: "/app/orders/$id/shipping-label",
                    params: { id },
                  })
                }
              >
                <MapPin data-icon="inline-start" />
                ປ້າຍທີ່ຢູ່
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() =>
                  nav({
                    to: "/app/orders/$id/summary-image",
                    params: { id },
                  })
                }
              >
                <Download data-icon="inline-start" />
                ຮູບສະຫຼຸບ
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                isLoading={summaryQuery.isFetching}
                onClick={copySummary}
              >
                <Copy data-icon="inline-start" />
                Copy ສະຫຼຸບ
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-2 text-sm">
            <Link
              to="/app/customers/$id"
              params={{ id: order.customer.id }}
              className="font-medium text-base text-foreground underline-offset-4 hover:underline"
            >
              {order.customer.name}
            </Link>
            {order.customer.phone ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Phone className="size-4" /> {order.customer.phone}
              </span>
            ) : null}
            {customerAddress ? (
              <span className="flex items-start gap-2 text-muted-foreground">
                <MapPin className="size-4 shrink-0" />
                <span className="whitespace-pre-line">{customerAddress}</span>
              </span>
            ) : null}
            <span className="flex items-start gap-2 text-muted-foreground">
              <Truck className="mt-0.5 size-4 shrink-0" />
              <span>{formatDeliverySummary(order)}</span>
            </span>
            <span className="text-muted-foreground">
              ຊຳລະ: {paymentTypeLabel(order.paymentType)}
            </span>
          </div>
        </ProductFormSectionLike>

        {(snippetsQuery.data?.data?.length ?? 0) > 0 ? (
          <ProductFormSectionLike icon={Copy} title="Snippet ດ່ວນ">
            <div className="flex flex-wrap gap-2">
              {snippetsQuery.data?.data.map((snippet) => (
                <Button
                  key={snippet.id}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => copySnippet(snippet.body)}
                >
                  {snippet.title}
                </Button>
              ))}
            </div>
          </ProductFormSectionLike>
        ) : null}

        <ProductFormSectionLike icon={ShoppingBag} title="ລາຍການສິນຄ້າ">
          <OrderDetailItemsPanel
            items={order.items}
            showCogs={showCogs}
            showDraftLots={order.status === "draft"}
          />

          <div className="mt-2 flex flex-col gap-1 border-secondary/20 border-t pt-3 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>ຍອດລວມສິນຄ້າ</span>
              <span className="tabular-nums">{formatLak(order.subtotal)}</span>
            </div>
            {Number(order.discountAmount) > 0 ? (
              <div className="flex justify-between text-muted-foreground">
                <span>ສ່ວນລົດ</span>
                <span className="text-destructive tabular-nums">
                  −{formatLak(order.discountAmount)}
                </span>
              </div>
            ) : null}
            {Number(order.shippingFee) > 0 ? (
              <div className="flex justify-between text-muted-foreground">
                <span>ຄ່າຈັດສົ່ງ</span>
                <span className="tabular-nums">
                  {formatLak(order.shippingFee)}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between font-display text-foreground text-lg">
              <span>ລວມ</span>
              <span className="text-secondary tabular-nums">
                {formatLak(order.total)}
              </span>
            </div>
          </div>

          {order.note ? (
            <p className="rounded-lg bg-muted/40 p-3 text-muted-foreground text-sm">
              {order.note}
            </p>
          ) : null}
        </ProductFormSectionLike>

        {order.shippedAt ||
        order.deliveredAt ||
        order.trackingNumber ||
        order.pickedUpAt ||
        order.returnedAt ? (
          <ProductFormSectionLike icon={Truck} title="ການສົ່ງ">
            <div className="flex flex-col gap-2 text-sm">
              {order.shippedAt ? (
                <span className="text-muted-foreground">
                  ສົ່ງເມື່ອ {formatDateTimeLocal(order.shippedAt)}
                </span>
              ) : null}
              {order.deliveredAt ? (
                <span className="text-muted-foreground">
                  ຂົນສົ່ງສົ່ງຮອດເມື່ອ {formatDateTimeLocal(order.deliveredAt)}
                </span>
              ) : null}
              {order.trackingNumber ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    Tracking:{" "}
                    <span className="font-mono">{order.trackingNumber}</span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-full"
                    onClick={() => setBarcodeOpen(true)}
                  >
                    ເບິ່ງ Barcode
                  </Button>
                </div>
              ) : null}
              {order.pickedUpAt ? (
                <span className="text-leaf">
                  ລູກຄ້າໄປເອົາເມື່ອ {formatDateTimeLocal(order.pickedUpAt)}
                </span>
              ) : null}
              {order.returnedAt ? (
                <span className="text-destructive">
                  ຕີກັບເມື່ອ {formatDateTimeLocal(order.returnedAt)}
                </span>
              ) : null}
              {order.returnNote ? (
                <p className="rounded-lg bg-destructive/10 p-3 text-muted-foreground text-sm">
                  {order.returnNote}
                </p>
              ) : null}
              {order.returnShippingCost || order.outboundCarrierCost ? (
                <div className="flex flex-col gap-1 text-sm">
                  {order.returnShippingCost ? (
                    <span>
                      ຄ່າສົ່ງຕີກັບ:{" "}
                      <span className="font-medium tabular-nums">
                        {formatLak(order.returnShippingCost)}
                      </span>
                    </span>
                  ) : null}
                  {order.outboundCarrierCost ? (
                    <span>
                      ຄ່າສົ່ງໄປ (ຮ້ານຈ່າຍ):{" "}
                      <span className="font-medium tabular-nums">
                        {formatLak(order.outboundCarrierCost)}
                      </span>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </ProductFormSectionLike>
        ) : null}

        {canManage && !isCancelled ? (
          <div className="flex flex-col gap-4">
            {(isPaid || isReadyToShip) && !isShipped ? (
              <div className="flex flex-col gap-2 sm:max-w-sm">
                <label
                  htmlFor="tracking-input"
                  className="text-muted-foreground text-sm"
                >
                  ເລກ Tracking (ຖ້າມີ)
                </label>
                <Input
                  id="tracking-input"
                  value={trackingInput}
                  onChange={(e) => setTrackingInput(e.target.value)}
                  placeholder="ເລກພັດສະດຸ..."
                />
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {isDraft ? (
                <>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() =>
                      nav({ to: "/app/orders/$id/edit", params: { id } })
                    }
                  >
                    <EditIcon data-icon="inline-start" />
                    ແກ້ໄຂ
                  </Button>
                  <Button
                    className="rounded-full"
                    isLoading={markPaid.isPending}
                    onClick={handleMarkPaid}
                  >
                    <CircleCheckIcon data-icon="inline-start" />
                    {order.paymentType === "cod" ? "ຢືນຢັນ COD" : "ໝາຍວ່າຊຳລະແລ້ວ"}
                  </Button>
                </>
              ) : null}
              {isPaid ? (
                <Button
                  variant="outline"
                  className="rounded-full"
                  isLoading={markReady.isPending}
                  onClick={handleMarkReady}
                >
                  <CircleCheckIcon data-icon="inline-start" />
                  ແພັກສຳເລັດ
                </Button>
              ) : null}
              {isPaid || isReadyToShip ? (
                <Button
                  className="rounded-full"
                  isLoading={markShipped.isPending}
                  onClick={handleMarkShipped}
                >
                  <Truck data-icon="inline-start" />
                  ສົ່ງແລ້ວ
                </Button>
              ) : null}
              {canMarkReturned ? (
                <Button
                  variant="outline"
                  className="rounded-full border-destructive/40 text-destructive"
                  onClick={handleMarkReturned}
                >
                  <RotateCcw data-icon="inline-start" />
                  ຂົນສົ່ງຕີກັບ
                </Button>
              ) : null}
                            {isShipped ? (
                <Button
                  variant="outline"
                  className="rounded-full"
                  isLoading={markDelivered.isPending}
                  onClick={handleMarkDelivered}
                >
                  <Truck data-icon="inline-start" />
                  ຂົນສົ່ງສົ່ງຮອດແລ້ວ
                </Button>
              ) : null}
              {isShipped || isDelivered ? (
                <Button
                  className="rounded-full"
                  isLoading={markPickedUp.isPending}
                  onClick={handleMarkPickedUp}
                >
                  <CircleCheckIcon data-icon="inline-start" />
                  ລູກຄ້າໄປເອົາແລ້ວ
                </Button>
              ) : null}
              {isReturned ? (
                <Button
                  className="rounded-full"
                  isLoading={reopenShipment.isPending}
                  onClick={handleReopenShipment}
                >
                  <RotateCcw data-icon="inline-start" />
                  ສົ່ງໃໝ່
                </Button>
              ) : null}
              {canRevert ? (
                <Button
                  variant="outline"
                  className="rounded-full"
                  isLoading={revertStatus.isPending}
                  onClick={handleRevertStatus}
                >
                  <RotateCcw data-icon="inline-start" />
                  ຍ້ອນສະຖານະ
                </Button>
              ) : null}
              {!isPickedUp ? (
                <Button
                  variant="outline"
                  className="rounded-full border-destructive/40 text-destructive"
                  isLoading={cancelOrder.isPending}
                  onClick={handleCancel}
                >
                  <XCircle data-icon="inline-start" />
                  ຍົກເລີກອໍເດີ້
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </Main>
      {canEditDetails ? (
        <OrderDetailsEditDialog
          order={order}
          open={detailsEditOpen}
          onOpenChange={setDetailsEditOpen}
        />
      ) : null}
      {canMarkReturned ? (
        <OrderMarkReturnedDialog
          order={order}
          open={returnDialogOpen}
          onOpenChange={setReturnDialogOpen}
        />
      ) : null}
      {order.trackingNumber ? (
        <TrackingBarcodeDialog
          open={barcodeOpen}
          onOpenChange={setBarcodeOpen}
          trackingNumber={order.trackingNumber}
        />
      ) : null}
    </>
  );
}
